import axios, { AxiosInstance, AxiosError, InternalAxiosRequestConfig } from 'axios'
import { bulkReplaceQrsCache, enqueueEvento } from './db'

const API_BASE = import.meta.env.VITE_API_BASE || 'http://localhost:8000/api/v1'

// Sprint 8: Render free tier puede tardar 30-60s en despertar tras hibernar.
// Subido de 10s a 60s para que el cold-start no produzca "Network Error" en
// la UI. Una vez Render esta caliente las requests caen a <2s. Si activamos
// UptimeRobot (ver docs/sprint8/RECONCILIAR_ODOO_RENDER_TEST.md) Render no
// hiberna y este timeout extendido es invisible al usuario en operacion.
export const api: AxiosInstance = axios.create({
  baseURL: API_BASE,
  timeout: 60_000,
})

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('avante_token')
  if (token) config.headers.Authorization = `Bearer ${token}`
  return config
})

// ============================================================
// Interceptor de respuesta — refresh flow (Sprint 3 H01-06b)
// ============================================================
// Mutex: si llega un 401 cuando ya hay un refresh en curso, las requests
// esperan al mismo refresh en lugar de dispararlo N veces (request storm).
let refreshInFlight: Promise<string | null> | null = null

// Endpoints que NO deben dispararar refresh (loop infinito).
const NO_REFRESH_PATHS = ['/auth/login', '/auth/refresh', '/auth/logout']

async function doRefresh(): Promise<string | null> {
  const refresh = localStorage.getItem('avante_refresh')
  if (!refresh) return null
  try {
    const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token: refresh })
    localStorage.setItem('avante_token', data.access_token)
    localStorage.setItem('avante_refresh', data.refresh_token)
    // Notificar al store zustand: el módulo lo lee de localStorage al rehidratar,
    // pero también disparamos un custom event para que la app actualice expires_at.
    window.dispatchEvent(new CustomEvent('avante:token-refreshed', {
      detail: { access_token: data.access_token, refresh_token: data.refresh_token, expires_in: data.expires_in },
    }))
    return data.access_token
  } catch {
    localStorage.removeItem('avante_token')
    localStorage.removeItem('avante_refresh')
    return null
  }
}

api.interceptors.response.use(
  (r) => r,
  async (err: AxiosError) => {
    const original = err.config as (InternalAxiosRequestConfig & { _retried?: boolean }) | undefined

    // No es 401, o no hay request original, o ya retried → propagar tal cual.
    if (err.response?.status !== 401 || !original || original._retried) {
      return Promise.reject(err)
    }
    // Endpoints excluidos (login/refresh/logout) → propagar (no retry).
    const path = (original.url || '').replace(API_BASE, '')
    if (NO_REFRESH_PATHS.some((p) => path.endsWith(p))) {
      return Promise.reject(err)
    }

    // Mutex: si ya hay un refresh en vuelo, esperamos su resultado.
    if (!refreshInFlight) {
      refreshInFlight = doRefresh().finally(() => {
        refreshInFlight = null
      })
    }
    const newToken = await refreshInFlight

    if (!newToken) {
      // Refresh falló → redirect a login. Limpiamos por si quedó residual.
      localStorage.removeItem('avante_token')
      localStorage.removeItem('avante_refresh')
      // Solo redirigir si no estamos ya en /login (evita reload loop).
      if (!window.location.pathname.startsWith('/login')) {
        window.location.href = '/login'
      }
      return Promise.reject(err)
    }

    // Retry el request original con el nuevo token.
    original._retried = true
    original.headers = original.headers || {}
    original.headers.Authorization = `Bearer ${newToken}`
    return api.request(original)
  },
)

// ============================================================
// API endpoints tipados
// ============================================================
export const authApi = {
  login: async (username: string, password: string) => {
    const form = new URLSearchParams({ username, password })
    const { data } = await axios.post(`${API_BASE}/auth/login`, form, {
      headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    })
    return data
  },
  // Sprint 3 H01-06: rotation flow. No usa el `api` instance porque
  // queremos saltarnos el interceptor 401 (evita loops si el refresh expira).
  refresh: async (refresh_token: string) => {
    const { data } = await axios.post(`${API_BASE}/auth/refresh`, { refresh_token })
    return data
  },
  logout: async (refresh_token: string) => {
    // Idempotente server-side; ignoramos response (204).
    await axios.post(`${API_BASE}/auth/logout`, { refresh_token }).catch(() => undefined)
  },
  me: () => api.get('/auth/me').then((r) => r.data),
}

export const auxiliarApi = {
  pendientes: () => api.get('/auxiliar/pendientes').then((r) => r.data),
  scanQR: (qr_token: string, comida_elegida_id: number, timestamp_local?: string) =>
    api.post('/auxiliar/qr/scan', { qr_token, comida_elegida_id, timestamp_local }).then((r) => r.data),
  excluirLinea: (comida_id: number, linea_id: number, motivo?: string) =>
    api.post(`/auxiliar/comida/${comida_id}/excluir-linea`, { linea_id, motivo }).then((r) => r.data),
  notaPaciente: (comida_id: number, notas: string) =>
    api.post(`/auxiliar/comida/${comida_id}/nota-paciente`, { notas }).then((r) => r.data),
  enviarCocina: (comida_id: number) =>
    api.post(`/auxiliar/comida/${comida_id}/enviar-cocina`).then((r) => r.data),
  entregar: (comida_id: number) =>
    api.post(`/auxiliar/comida/${comida_id}/entregar`).then((r) => r.data),
  // S8 T43 - el paciente elige UNA opcion del menu; las otras se excluyen
  elegirOpcion: (comida_id: number, menu_opcion_id: number) =>
    api.post(`/auxiliar/comida/${comida_id}/elegir-opcion`, { menu_opcion_id }).then((r) => r.data),
  // S8 T45 - sincroniza la comida con opciones nuevas del menu vigente
  refrescarOpciones: (comida_id: number): Promise<{
    comida_id: number; lineas_nuevas_agregadas: number; lineas_totales: number
  }> => api.post(`/auxiliar/comida/${comida_id}/refrescar-opciones`).then((r) => r.data),
}

export const cocinaApi = {
  cola: () => api.get('/cocina/cola').then((r) => r.data),
  enPreparacion: (comida_id: number) =>
    api.post(`/cocina/comida/${comida_id}/en-preparacion`).then((r) => r.data),
  marcarLista: (comida_id: number) =>
    api.post(`/cocina/comida/${comida_id}/lista`).then((r) => r.data),
}

export const syncApi = {
  pushBatch: (eventos: any[]) => api.post('/eventos/sync', { eventos }).then((r) => r.data),
}

// ============================================================
// Chef (Fase B) — solicitudes de compra
// ============================================================
export interface AlimentoLite {
  id: number
  codigo: string
  nombre: string
}

export interface SolicitudCompra {
  id: number
  sede_id: number
  alimento_id: number
  cantidad_solicitada_g: string  // Decimal serializado como string por Pydantic
  motivo: string | null
  estado: string
  solicitado_por_usuario_id: number
  solicitado_at: string
  email_enviado_at: string | null
  email_destinatario: string | null
  email_error: string | null
  aprobada_at: string | null
  aprobada_por: string | null
  recibida_at: string | null
  lote_id_recibido: number | null
  cancelada_at: string | null
  cancelacion_motivo: string | null
}

export interface PlatoEnCola {
  plato_id: number
  plato_codigo: string
  plato_nombre: string
  porciones: number
}

export interface BomItem {
  alimento_id: number
  alimento_codigo: string
  alimento_nombre: string
  cantidad: number
  unidad: string
  cantidad_en_gramos_calc: number | null
  es_principal: boolean
  es_opcional: boolean
  orden: number
  observacion: string | null
}

export interface BomResponse {
  plato_id: number
  plato_codigo: string
  plato_nombre: string
  ingredientes: BomItem[]
}

export interface BomAgregadoItem {
  alimento_id: number
  alimento_codigo: string
  alimento_nombre: string
  gramos_totales: number | null
  porciones_que_lo_usan: number
  platos_distintos: number
}

export interface InventarioItem {
  id: number
  alimento_id: number
  alimento_codigo: string
  alimento_nombre: string
  categoria: string | null
  sede_id: number
  stock_actual_g: number
  stock_actual_unidades: number
  punto_reorden_g: number | null
  stock_minimo_g: number | null
  stock_maximo_g: number | null
  tipo: string
  stock_bajo: boolean
  ultimo_movimiento_at: string | null
}

export interface AlertasStockResponse {
  bajo_stock_count: number
  lotes_vencen_3d_count: number
  lotes_vencidos_count: number
  total_alertas: number
  bajo_stock_preview: Array<{
    alimento_codigo: string
    alimento_nombre: string
    stock_actual_g: number
    punto_reorden_g: number | null
  }>
  lotes_vencen_preview: Array<{
    codigo_lote: string
    alimento_nombre: string
    fecha_vencimiento: string
    cantidad_actual_g: number
  }>
}

export interface ConteoFisicoResponse {
  lote_id: number
  codigo_lote: string
  cantidad_g: number
  fecha_vencimiento: string
  stock_total_g: number
}

export interface AjusteLoteResponse {
  lote_id: number
  codigo_lote: string
  cantidad_anterior_g: number
  cantidad_real_g: number
  diferencia_g: number
  tipo_movimiento: string
  estado_lote: string
  stock_total_g: number | null
}

export interface LoteInventario {
  id: number
  codigo_lote: string
  sede_id: number
  cantidad_inicial_g: number
  cantidad_actual_g: number
  fecha_recepcion: string
  fecha_vencimiento: string
  estado: string
  proveedor: string | null
  costo_unitario: number | null
  notas: string | null
}

export const chefApi = {
  listarAlimentos: (q?: string) =>
    api.get<AlimentoLite[]>('/chef/alimentos', { params: q ? { q } : undefined }).then((r) => r.data),
  bomAgregado: () => api.get<BomAgregadoItem[]>('/chef/cola/bom-agregado').then((r) => r.data),
  listarInventario: (params?: { tipo?: string; q?: string; solo_bajo_stock?: boolean }) =>
    api.get<InventarioItem[]>('/chef/inventario', { params }).then((r) => r.data),
  lotesAlimento: (alimento_id: number) =>
    api.get<LoteInventario[]>(`/chef/inventario/${alimento_id}/lotes`).then((r) => r.data),
  ajustarLote: (lote_id: number, body: { cantidad_real_g: number; motivo: string; notas?: string }) =>
    api.post<AjusteLoteResponse>(`/chef/inventario/lotes/${lote_id}/ajustar`, body).then((r) => r.data),
  conteoFisico: (
    alimento_id: number,
    body: { cantidad_g: number; fecha_vencimiento: string; codigo_lote?: string; notas?: string },
  ) =>
    api
      .post<ConteoFisicoResponse>(`/chef/inventario/${alimento_id}/conteo-fisico`, body)
      .then((r) => r.data),
  alertasStock: () => api.get<AlertasStockResponse>('/chef/alertas-stock').then((r) => r.data),
  listarSolicitudes: (params?: { estado?: string; sede_id?: number }) =>
    api.get<SolicitudCompra[]>('/chef/solicitudes-compra', { params }).then((r) => r.data),
  crearSolicitud: (body: { alimento_id: number; cantidad_solicitada_g: number; motivo?: string }) =>
    api.post<SolicitudCompra>('/chef/solicitud-compra', body).then((r) => r.data),
  enviarEmail: (id: number) =>
    api.post<SolicitudCompra>(`/chef/solicitud-compra/${id}/enviar-email`).then((r) => r.data),
  aprobar: (id: number, aprobada_por: string) =>
    api.post<SolicitudCompra>(`/chef/solicitud-compra/${id}/aprobar`, { aprobada_por }).then((r) => r.data),
  recibir: (
    id: number,
    body: {
      lote_id_recibido?: number
      codigo_lote?: string
      cantidad_recibida_g?: number
      fecha_vencimiento?: string
      proveedor?: string
      numero_factura?: string
      costo_unitario?: number
      notas?: string
    } = {},
  ) =>
    api.post<SolicitudCompra>(`/chef/solicitud-compra/${id}/recibir`, body).then((r) => r.data),
  cancelar: (id: number, motivo: string) =>
    api.post<SolicitudCompra>(`/chef/solicitud-compra/${id}/cancelar`, { motivo }).then((r) => r.data),
  // Fase B paso 2 - BOM
  platosEnCola: () => api.get<PlatoEnCola[]>('/chef/cola/platos-unicos').then((r) => r.data),
  bomPlato: (plato_id: number) =>
    api.get<BomResponse>(`/chef/plato/${plato_id}/bom`).then((r) => r.data),
}

// ============================================================
// Enfermería (Sprint 2 Tarea 2.7) — pantalla para registrar indicaciones
// ============================================================
export interface IndicacionCreatePayload {
  habitacion_codigo: string
  paciente_nombre: string
  paciente_documento: string | null
  dieta_codigo: string
  tiempo_codigo: string
  dia_semana: number
  indicacion_dr: string
  notas: string | null
}

export interface CoberturaMenu {
  dia_semana: number
  celdas: { dieta_codigo: string; tiempo_codigo: string }[]
}

export const enfermeriaApi = {
  dietas: () => api.get('/enfermeria/dietas').then((r) => r.data),
  tiempos: () => api.get('/enfermeria/tiempos').then((r) => r.data),
  pacientes: () => api.get('/enfermeria/pacientes').then((r) => r.data),
  cobertura: (dia_semana: number) =>
    api
      .get<CoberturaMenu>('/enfermeria/cobertura', { params: { dia_semana } })
      .then((r) => r.data),
  crearIndicacion: (payload: IndicacionCreatePayload) =>
    api.post('/enfermeria/indicacion', payload).then((r) => r.data),
  indicacionesHoy: () => api.get('/enfermeria/indicaciones-hoy').then((r) => r.data),
}

export const qrApi = {
  listaSede: () => api.get('/qr/lista-sede').then((r) => r.data),
}

// ============================================================
// Alergias del paciente (Sprint 5 T12-v2 — fuente PACIENTE_VIA_AUXILIAR)
// ============================================================
export type SeveridadAlergia = 'LEVE' | 'MODERADA' | 'SEVERA' | 'ANAFILACTICA' | 'DESCONOCIDA'
export type TipoAlergia = 'ALERGIA' | 'INTOLERANCIA' | 'PREFERENCIA_PERMANENTE'
export type TipoAlimentoAlergia = 'alimento' | 'fruta' | 'grupo_alimentario'
export type FuenteAlergia =
  | 'PACIENTE_VIA_AUXILIAR'
  | 'PACIENTE_VIA_ENFERMERA'
  | 'DOCTOR'
  | 'EXPEDIENTE_MEDICO'
  | 'FAMILIAR'
  | 'OTRO'

// BUGFIX 5-jul (QA ALTO): este tipo NUNCA coincidió con el schema del backend
// (mandaba `tipo`/`fuente`/`notas` en vez de `tipo_restriccion`/`tipo_alimento`/
// `motivo_clinico`) -> toda alta de alergia daba 422 y NINGUNA se guardaba.
// `fuente` se DERIVA server-side del rol; no se envía.
export interface AlergiaCreate {
  paciente_id: number
  tipo_alimento: TipoAlimentoAlergia
  alimento_id: number // 0 cuando tipo_alimento='grupo_alimentario'
  grupo_alimentario?: string | null
  tipo_restriccion: TipoAlergia
  severidad?: SeveridadAlergia | null
  motivo_clinico?: string | null
  fecha_fin?: string | null
  validada_clinicamente?: boolean | null
}

export interface AlergiaResponse {
  id: number
  paciente_id: number
  alimento_id: number | null
  alimento_nombre: string | null
  grupo_alimentario: string | null
  tipo: TipoAlergia
  severidad: SeveridadAlergia
  fuente: FuenteAlergia
  notas: string | null
  activa: boolean
  contradicha: boolean
  created_at: string
  created_by_usuario_id: number | null
}

export interface IngredienteSugerido {
  id: number
  nombre: string
  grupo_alimentario?: string | null
  // QA 6-jul: la marca de alergeno permite advertir en el combobox y confirma
  // que al seleccionarlo se activara el bloqueo por grupo Codex.
  es_alergeno?: boolean
  tipo_alergeno?: string | null
}

// ============================================================
// Sustituciones equivalentes (Fase E)
// ============================================================
export interface SustitucionRanked {
  sustitucion_id: number
  plato_id_sustituto: number
  plato_codigo_sustituto: string
  plato_nombre_sustituto: string
  motivo_tipificado: string
  score_equivalencia: number
}

export const sustitucionesApi = {
  topSustitutos: async (plato_id: number, paciente_id: number): Promise<SustitucionRanked[]> => {
    try {
      const r = await api.get<SustitucionRanked[]>(
        `/sustituciones/plato/${plato_id}/paciente/${paciente_id}`,
      )
      return r.data
    } catch (err: any) {
      // Tolerante a 404 (sin sustituciones cargadas) -> devuelve []
      if (err?.response?.status === 404) return []
      throw err
    }
  },
}

export const alergiasApi = {
  // GET /alergias-paciente?paciente_id=N
  // Si el endpoint aun no esta merged en main (T9 puede estar pendiente)
  // capturamos 404/Network y devolvemos [] para que el flujo no rompa.
  listar: async (paciente_id: number): Promise<AlergiaResponse[]> => {
    try {
      const { data } = await api.get('/alergias-paciente', { params: { paciente_id } })
      return Array.isArray(data) ? data : []
    } catch (err: any) {
      const status = err?.response?.status
      // 404 = endpoint no existe aun. Network error = backend caido. En ambos
      // casos seguimos sin alergias en memoria — degradacion elegante (Δ).
      if (status === 404 || !err?.response) return []
      throw err
    }
  },

  crear: (payload: AlergiaCreate): Promise<AlergiaResponse> =>
    api.post('/alergias-paciente', payload).then((r) => r.data),
}

export const ingredientesApi = {
  // Autocomplete de alimentos del catalogo para el modal de captura de alergias.
  // QA 6-jul (CRITICO): antes llamaba a `/ingredientes` (404 para el auxiliar:
  // el endpoint real vive bajo /nutricionista y exige rol nutricionista/admin),
  // asi que el combobox nunca mostraba sugerencias y forzaba texto libre que NO
  // bloqueaba. Ahora usa `/catalogo/alimentos` (router publico, accesible a
  // auxiliar/enfermera) que si busca por nombre y marca los alergenos.
  buscar: async (query: string): Promise<IngredienteSugerido[]> => {
    if (query.trim().length < 2) return []
    try {
      const { data } = await api.get('/catalogo/alimentos', { params: { q: query, limit: 12 } })
      return Array.isArray(data) ? data : []
    } catch (err: any) {
      const status = err?.response?.status
      if (status === 404 || !err?.response) return []
      throw err
    }
  },
}

// ============================================================
// Offline-first wrappers (Sprint 2 Tarea 2.6)
// ============================================================
export interface OutboxMeta {
  comida_id: number
  tipo_evento: string
  qr_token?: string
}

export interface OfflineResult<T = any> {
  status: 'sent' | 'queued'
  data?: T
}

/**
 * Wrapper para POSTs offline-aware. Si hay red, hace la mutación normal.
 * Si la red falla o el navegador está offline, encola el evento en outbox.
 *
 * Solo se considera "offline" cuando hay error de red (sin response).
 * Errores 4xx/5xx burbujean al caller — son fallas reales, no transitorias.
 */
export async function postWithOutboxFallback<T = any>(
  endpoint: string,
  payload: any,
  meta: OutboxMeta,
): Promise<OfflineResult<T>> {
  if (!navigator.onLine) {
    await enqueueEvento(meta.comida_id, meta.tipo_evento, payload, meta.qr_token)
    return { status: 'queued' }
  }
  try {
    const { data } = await api.post(endpoint, payload)
    return { status: 'sent', data }
  } catch (err: any) {
    const isNetworkError = !err.response || err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED'
    if (isNetworkError) {
      await enqueueEvento(meta.comida_id, meta.tipo_evento, payload, meta.qr_token)
      return { status: 'queued' }
    }
    throw err
  }
}

/**
 * Refresca cache local de QRs desde el backend. Layer 1 (al login),
 * Layer 2 (TTL expirado) y Layer 3 (404 reactiva) la invocan.
 */
export async function refreshQrsCache(): Promise<void> {
  const qrs = await qrApi.listaSede()
  await bulkReplaceQrsCache(qrs)
}

// ============================================================
// Sprint 9 C3 — Tarifas (plan reunion 12-jun)
// ============================================================
export interface TarifaDieta {
  id: number
  codigo: string
  nombre: string
  activo: boolean
  precio_unit: number | null
  precio_efectivo: number
}

export const tarifasApi = {
  listar: () => api.get<TarifaDieta[]>('/tarifas/dietas').then((r) => r.data),
  actualizar: (id: number, precio_unit: number | null) =>
    api
      .patch<TarifaDieta>(`/tarifas/dietas/${id}`, { precio_unit })
      .then((r) => r.data),
  getDefault: () =>
    api.get<{ price_unit_default: number }>('/tarifas/default').then((r) => r.data),
  setDefault: (price_unit_default: number) =>
    api
      .put<{ price_unit_default: number }>('/tarifas/default', { price_unit_default })
      .then((r) => r.data),
}

// ============================================================
// Sprint 9 C4 — Cafetería
// ============================================================
export interface CafeteriaProducto {
  id: number
  codigo: string
  nombre: string
  categoria: string | null
  precio_unit: number
  foto_url: string | null
  descripcion: string | null
  activo: boolean
}

export type ModoPago = 'EFECTIVO' | 'TARJETA' | 'CARGO_PACIENTE'

export interface CafeteriaOrdenLinea {
  id: number
  producto_id: number
  producto_nombre: string | null
  cantidad: number
  precio_unit: number
  subtotal: number
}

export interface CafeteriaOrden {
  id: number
  sede_id: number
  cliente_id: number | null
  cliente_nombre: string | null
  cajero_id: number
  modo_pago: ModoPago
  total: number
  estado: 'ABIERTA' | 'CONFIRMADA' | 'CANCELADA'
  created_at: string
  confirmada_at: string | null
  cancelada_at: string | null
  cancelacion_motivo: string | null
  cargo_estado: string
  cargo_account_move_line_id: number | null
  cargo_error_msg: string | null
  lineas: CafeteriaOrdenLinea[]
}

export interface CafeteriaOrdenCreate {
  sede_id: number
  cliente_id: number | null
  modo_pago: ModoPago
  lineas: { producto_id: number; cantidad: number }[]
}

export const cafeteriaApi = {
  // Catálogo
  productos: () =>
    api.get<CafeteriaProducto[]>('/cafeteria/productos').then((r) => r.data),
  productosAdmin: (activo?: boolean) =>
    api
      .get<CafeteriaProducto[]>('/cafeteria/admin/productos', {
        params: activo !== undefined ? { activo } : {},
      })
      .then((r) => r.data),
  crearProducto: (data: Omit<CafeteriaProducto, 'id'>) =>
    api.post<CafeteriaProducto>('/cafeteria/admin/productos', data).then((r) => r.data),
  actualizarProducto: (id: number, data: Partial<CafeteriaProducto>) =>
    api
      .patch<CafeteriaProducto>(`/cafeteria/admin/productos/${id}`, data)
      .then((r) => r.data),

  // Órdenes
  crearOrden: (payload: CafeteriaOrdenCreate) =>
    api.post<CafeteriaOrden>('/cafeteria/ordenes', payload).then((r) => r.data),
  listarOrdenes: (params?: { sede_id?: number; estado?: string; fecha?: string }) =>
    api
      .get<CafeteriaOrden[]>('/cafeteria/ordenes', { params })
      .then((r) => r.data),
  getOrden: (id: number) =>
    api.get<CafeteriaOrden>(`/cafeteria/ordenes/${id}`).then((r) => r.data),
  checkout: (id: number) =>
    api
      .post<CafeteriaOrden>(`/cafeteria/ordenes/${id}/checkout`)
      .then((r) => r.data),
  cancelar: (id: number, motivo: string) =>
    api
      .post<CafeteriaOrden>(`/cafeteria/ordenes/${id}/cancelar`, { motivo })
      .then((r) => r.data),
}

// ============================================================
// Sprint 9 C5 — Recepción (cuenta abierta del paciente)
// ============================================================
export interface CuentaAbiertaItem {
  cliente_id: number
  nombre: string
  habitacion: string
  sede_id: number
  fecha_ingreso: string | null
  comidas_entregadas: number
  cafeteria_ordenes: number
  total_dietas: number
  total_cafeteria: number
  total: number
}

export interface DetalleLineaItem {
  tipo: 'DIETA' | 'CAFETERIA'
  fecha: string
  descripcion: string
  cantidad: number
  precio_unit: number
  subtotal: number
  cargo_estado: string
}

export interface CuentaAbiertaDetalle {
  cliente_id: number
  nombre: string
  habitacion: string
  sede_id: number
  items: DetalleLineaItem[]
  total_dietas: number
  total_cafeteria: number
  total: number
}

export const recepcionApi = {
  listar: (sede_id?: number) =>
    api
      .get<CuentaAbiertaItem[]>('/recepcion/cuentas-abiertas', {
        params: sede_id ? { sede_id } : {},
      })
      .then((r) => r.data),
  detalle: (cliente_id: number) =>
    api
      .get<CuentaAbiertaDetalle>(`/recepcion/cuentas-abiertas/${cliente_id}`)
      .then((r) => r.data),
  cerrarCuenta: (cliente_id: number) =>
    api
      .post<CerrarCuentaResponse>(`/recepcion/cuentas-abiertas/${cliente_id}/cerrar`)
      .then((r) => r.data),
}

export interface CerrarCuentaResponse {
  cliente_id: number
  nombre: string
  total_dietas: number
  total_cafeteria: number
  total_cobrado: number
  egreso_at: string
}
